/**
 * Course Catalog Adapter Service
 *
 * Provides a single normalized course shape regardless of whether the data
 * originated from the static courses.js file (rich syllabus content) or the
 * backend API (simpler database-driven records).
 *
 * Normalized course shape:
 * {
 *   id, slug, title, code, programType, description, duration, level,
 *   category, fee, image, advantages, whatYouCanDo, jobOpportunities,
 *   prerequisites, isNew, source
 * }
 *
 * Source resolution:
 *   REGULAR  → courses.js (static, rich content)
 *   HOLIDAY  → backend API (/auth/courses?program_type=HOLIDAY)
 *   BOOTCAMP → backend API (/auth/courses?program_type=BOOTCAMP)
 *   CORPORATE→ backend API (/auth/courses?program_type=CORPORATE)
 */

import coursesData from '../data/courses'
import { courseImages } from '../components/landing/landingData'
import api from './api'
import {
  PROGRAM_TYPE_LABELS,
  PROGRAM_TYPE_ORDER,
  normalizeProgramType,
} from '../utils/programTypes'

// ─── Normalizers ───────────────────────────────────────────────────────────

/**
 * Normalize a static courses.js entry into the unified shape.
 */
function normalizeStaticCourse(course) {
  return {
    id: course.code,
    slug: course.url?.replace('/courses/', '') || course.code,
    title: course.title || 'Untitled Program',
    code: course.code || '',
    programType: 'REGULAR',
    description: course.description || '',
    duration: course.duration || '',
    level: course.level || 'Beginner',
    category: course.category || 'Multimedia',
    fee: course.fee || '',
    image: courseImages[course.code] || courseImages.default,
    advantages: course.advantages || [],
    whatYouCanDo: course.whatYouCanDo || [],
    jobOpportunities: course.jobOpportunities || [],
    prerequisites: course.prerequisites || [],
    isNew: course.isNew || false,
    source: 'static',
  }
}

/**
 * Normalize a backend API course record into the unified shape.
 * API fields: id, course_name, course_code, description, duration,
 *             duration_weeks, fee, category, program_type, level
 */
function normalizeApiCourse(raw) {
  const programType = normalizeProgramType(raw.program_type)
  const fee = raw.fee != null
    ? (typeof raw.fee === 'number'
        ? 'UGX ' + raw.fee.toLocaleString('en-US')
        : String(raw.fee))
    : ''
  const duration = raw.duration || (raw.duration_weeks ? `${raw.duration_weeks} weeks` : '')

  return {
    id: raw.id || raw.course_code,
    slug: raw.course_code || String(raw.id || ''),
    title: raw.course_name || raw.title || 'Untitled Program',
    code: raw.course_code || '',
    programType,
    description: raw.description || '',
    duration,
    level: raw.level || 'Beginner',
    category: raw.category || 'General',
    fee,
    image: courseImages[raw.course_code] || courseImages.default,
    advantages: raw.advantages || [],
    whatYouCanDo: raw.whatYouCanDo || [],
    jobOpportunities: raw.jobOpportunities || [],
    prerequisites: raw.prerequisites || [],
    isNew: false,
    source: 'api',
  }
}

// ─── Public API ────────────────────────────────────────────────────────────

/**
 * Fetch courses for a given program type.
 * REGULAR → static courses.js (synchronous, returns a Promise for uniformity)
 * HOLIDAY / BOOTCAMP / CORPORATE → backend API
 *
 * @param {string} programType — one of PROGRAM_TYPE_ORDER
 * @returns {Promise<Array<NormalizedCourse>>}
 */
export async function fetchCoursesByProgramType(programType) {
  const type = normalizeProgramType(programType)

  if (type === 'REGULAR') {
    return coursesData.map(normalizeStaticCourse)
  }

  try {
    const { data } = await api.get('/auth/courses', {
      params: { program_type: type },
    })
    const rows = Array.isArray(data) ? data : []
    return rows.map(normalizeApiCourse)
  } catch (err) {
    console.error(`[courseCatalog] Failed to fetch ${type} courses:`, err)
    return []
  }
}

/**
 * Fetch all courses across all program types.
 * Merges static REGULAR courses with API-fetched HOLIDAY/BOOTCAMP/CORPORATE.
 * Deduplicates by course code — if a code exists in both static and API,
 * the static (rich) version wins.
 *
 * @returns {Promise<Array<NormalizedCourse>>}
 */
export async function fetchAllCourses() {
  const results = await Promise.all(
    PROGRAM_TYPE_ORDER.map((type) => fetchCoursesByProgramType(type))
  )
  const merged = results.flat()

  // Deduplicate by code — static source wins over API
  const seen = new Map()
  for (const course of merged) {
    const key = course.code || course.title
    if (!seen.has(key) || course.source === 'static') {
      seen.set(key, course)
    }
  }
  return Array.from(seen.values())
}

/**
 * Group courses by program type.
 * @param {Array<NormalizedCourse>} courses
 * @returns {Object<string, Array<NormalizedCourse>>}
 */
export function groupByProgramType(courses) {
  const groups = {}
  for (const type of PROGRAM_TYPE_ORDER) groups[type] = []
  for (const course of courses) {
    const type = normalizeProgramType(course.programType)
    if (!groups[type]) groups[type] = []
    groups[type].push(course)
  }
  return groups
}

/**
 * Get the display label for a program type.
 */
export function getProgramTypeLabel(type) {
  return PROGRAM_TYPE_LABELS[normalizeProgramType(type)] || type
}

export { PROGRAM_TYPE_ORDER, PROGRAM_TYPE_LABELS }
